// shared-errors.data.ts
//
// Codes with no single owner: identity resolution, contact details the practice
// cannot reach, and the fallbacks every flow depends on.

import { ErrorEntry } from './error-metadata.types';

export const sharedErrors = {
  'unknown-error': {
    message: 'Unknown error. No code was matched',
  },
  'browser-action-issue': {
    message: 'The page reloaded and your progress could not be saved. Go back and try again.',
    title: 'The page reloaded unexpectedly',
    showStartButton: false,
    messageNhsAppWeb:
      'The page reloaded and your progress could not be saved. Close this tab and try again',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'unsupported-file-type': {
    message: 'Please upload in one of the following formats: JPEG, PNG & Word.',
    title: 'Unsupported file type',
  },
  'user-session-invalid': {
    message:
      'User not authorised to view this page. If you were submitting a request, it was not sent.',
    title: 'Error',
  },
  'logged-in-patient-not-found': {
    message:
      'It was not possible to match the information provided to a patient record at this practice.',
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'single-patient-not-found': {
    message:
      'It was not possible to match the information provided to a patient record at this practice.',
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'no-active-patient-found': {
    message:
      'It was not possible to match the information provided to a patient record at this practice.',
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'multiple-patient-matches': {
    message:
      'It’s not possible to continue online because the details entered match more than one record. Please contact your GP practice.',
    title: 'Unable to continue online',
  },
  'no-patient-matches': {
    message: 'The details you entered do not match our records. Please check them and try again.',
    title: 'We could not find your details',
  },
  'patient-not-active': {
    message:
      'It’s not possible to continue with this request online. Please contact your GP practice.',
    title: 'Unable to continue online',
  },
  'request-submission-patient-details-not-complete': {
    message:
      'It was not possible to submit your request online due to an issue with your patient details.',
    title: 'Request submission failed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-no-email-address-or-mobile-number': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'proxy-patient-has-no-email-address-or-mobile-number': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for this patient.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-no-email-address': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid email address on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-invalid-email-address': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid email address on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-no-mobile-number': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid mobile number on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-invalid-mobile-number': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid mobile number on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
  'patient-has-invalid-email-address-and-mobile-number': {
    message:
      'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for you.',
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },
} satisfies Record<string, ErrorEntry>;
